import { useCallback } from 'react'

import { indexes } from '@mab-common/utils'

import type { MsearchRequest } from '@elastic/elasticsearch/lib/api/types'

import useSWR from 'swr'

import {
  buildElasticAggsToHits,
  buildElasticToHits,
  buildMappedElasticHits,
} from '~/services/elastic/builder'
import { elasticClientFetcher } from '~/services/elastic/client'
import RecordTypes = indexes.RecordTypes

export function useElasticQuery<T>(
  key: RecordTypes,
  query: MsearchRequest,
  fallbackData?: any
) {
  // useSWR expects a value which will not change. We can't use [query, params] as currently,
  // a query and params could be totally new objects (even if they have the same content).
  // So we make a key which, the identity of which is totally dependent on the content of
  // the query.

  const getKey = useCallback(() => query, [query])
  const { data, error } = useSWR(
    getKey,
    async (props: MsearchRequest) => {
      const data = await elasticClientFetcher(props)
      let aggsOrHits = buildElasticToHits(key, data?.[0])
      if (!aggsOrHits.length) {
        aggsOrHits = buildElasticAggsToHits(data?.[0])
      }
      return buildMappedElasticHits(aggsOrHits, 'hits')
    },
    {
      shouldRetryOnError: true,
      errorRetryInterval: 1000,
      errorRetryCount: 2,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      fallbackData: fallbackData && fallbackData,
    }
  )

  return {
    type: data?.[0]?.type ?? '',
    total: data?.[0]?.total ?? 0,
    data: data?.[0]?.hits ?? null,
    error: error,
    isLoading: !error && !data,
  } as {
    insightsQueryID: string
    type: string
    total: number
    data: T[]
    error: any
    isLoading: boolean
  }
}

export function useElasticQueryMultiMap(
  keys: { recordType: RecordTypes; name: string }[],
  query: MsearchRequest,
  fallbackData?: any
) {
  const getKey = useCallback(() => query, [query])
  const { data, error } = useSWR(
    getKey,
    async (props: MsearchRequest) => {
      const data = await elasticClientFetcher(props)
      return keys.map((k, idx) => {
        let aggsOrHits = buildElasticToHits(k.name, data?.[idx])
        if (!aggsOrHits.length) {
          aggsOrHits = buildElasticAggsToHits(data?.[idx])
        }
        return buildMappedElasticHits(aggsOrHits, 'data', k.recordType)
      })
    },
    {
      shouldRetryOnError: true,
      errorRetryInterval: 1000,
      errorRetryCount: 2,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      fallbackData: fallbackData && fallbackData,
    }
  )

  return {
    data: data?.map((d: any) => d?.[0] || {}),
    error,
    isLoading: !error && !data,
  } as {
    data: {
      data: any[]
      type: string
      total: number
      insightsQueryID: string
    }[]
    error: any
    isLoading: boolean
  }
}
